import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SearchResultItem, SearchResultKind } from './buildSearchResultItems';

export type SearchPanelStatus = 'searching' | 'error' | 'ready';

type SearchResultsPanelProps = {
  listId: string;
  items: SearchResultItem[];
  activeIndex: number;
  status: SearchPanelStatus;
  term: string;
  onHover: (index: number) => void;
  onChoose: (item: SearchResultItem) => void;
};

const ICONS: Record<SearchResultKind, ReactNode> = {
  screen: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M9 20h6M12 16v4" />
    </>
  ),
  playlist: (
    <>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-5-5L5 21" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="5" width="14" height="14" rx="2" />
      <path d="m17 10 4-2v8l-4-2" />
    </>
  ),
};

export function optionId(listId: string, index: number): string {
  return `${listId}-option-${index}`;
}

function Message({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 px-4 py-3.5 text-sm" style={{ color: 'var(--gs-muted)' }}>
      {children}
    </p>
  );
}

/** Dropdown under the header search: grouped hits with keyboard highlight. */
export function SearchResultsPanel({
  listId,
  items,
  activeIndex,
  status,
  term,
  onHover,
  onChoose,
}: SearchResultsPanelProps) {
  const { t } = useTranslation(['layout', 'common']);
  return (
    <div
      className="absolute start-0 end-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border shadow-[0_12px_32px_rgba(20,20,20,0.12)]"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      {status === 'error' ? (
        <Message>{t('search.unavailable')}</Message>
      ) : items.length === 0 ? (
        <Message>
          {status === 'searching' ? t('common:states.searching') : t('search.noMatches', { term })}
        </Message>
      ) : (
        <ul id={listId} role="listbox" aria-label={t('search.results')} className="m-0 max-h-[420px] list-none overflow-y-auto p-1.5">
          {items.map((item, index) => {
            const startsGroup = index === 0 || items[index - 1]?.group !== item.group;
            const active = index === activeIndex;
            return (
              <li key={item.key} role="presentation">
                {startsGroup ? (
                  <span
                    className="block px-2.5 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.12em]"
                    style={{ color: 'var(--gs-muted)' }}
                    aria-hidden
                  >
                    {t(`search.groups.${item.group}`)}
                  </span>
                ) : null}
                <div
                  id={optionId(listId, index)}
                  role="option"
                  aria-selected={active}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm"
                  style={{
                    background: active ? 'var(--gs-panel2)' : 'transparent',
                    color: 'var(--gs-ink)',
                  }}
                  onMouseEnter={() => onHover(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onChoose(item)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                    style={{ color: 'var(--gs-muted)' }}
                    aria-hidden
                  >
                    {ICONS[item.kind]}
                  </svg>
                  <span className="min-w-0 flex-1 truncate font-semibold">{item.label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
